// PinStar — pin / unpin affordance for entity-row hover.
//
// Platform primitive (Iron Rule #11): every entity browser that wants a
// "favourite" or "pin to top" action renders this. Reused from /horizon,
// /terminal/closing-soon, /awards, and any future vendor / agency surface.
//
// UX contract:
//   * 16px square slot, fixed width — no row-height shift between
//     unpinned and pinned states. The character `★` and `☆` share the same
//     em advance in our mono font, but the slot is forced to a fixed box
//     for safety.
//   * Hover-revealed when the row is unpinned (`opacity: 0` until the row
//     hovers, then full opacity). Always-visible when pinned, so users can
//     spot pinned rows at a glance.
//   * Click toggles. Stops propagation so the row's onClick (navigate to
//     brief) does not fire when the user is just pinning.
//   * Keyboard: focusable, Space/Enter toggles.
//   * a11y: role="switch" + aria-checked + aria-label.
//
// Why a CSS-class trigger rather than a per-row JS hover state: the
// VirtualTable renders rows by the hundred (windowed). A JS hover state
// would force a render on every mouseover/mouseout. The hover style is
// driven by the parent row carrying `.pinstar-row` and `:hover .pinstar`
// rules — zero React work on hover.

import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';

export interface PinStarProps {
  pinned: boolean;
  onToggle: () => void;
  /** Optional label for screen readers (e.g. "pin opportunity ID-4271"). */
  label?: string;
  /** Force always-visible (e.g. in a column where the row hover trick isn't viable). */
  alwaysVisible?: boolean;
}

export function PinStar({ pinned, onToggle, label, alwaysVisible }: PinStarProps) {
  const aria = label ?? (pinned ? 'unpin row' : 'pin row');

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };
  const handleKey = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={pinned}
      aria-label={aria}
      title={aria}
      onClick={handleClick}
      onKeyDown={handleKey}
      data-pinstar
      data-pinned={pinned ? 'true' : 'false'}
      className={`pinstar${pinned ? ' pinstar-on' : ''}${alwaysVisible ? ' pinstar-vis' : ''}`}
      style={buttonStyle(pinned, alwaysVisible)}
    >
      {pinned ? '★' : '☆'}
    </button>
  );
}

function buttonStyle(pinned: boolean, alwaysVisible: boolean | undefined): CSSProperties {
  return {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    padding: 0,
    margin: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
    fontSize: 13,
    lineHeight: 1,
    color: pinned ? 'var(--amber, #d6a93b)' : 'var(--fg-3, #8a8a8a)',
    // Hover visibility:
    //  * unpinned → opacity 0 by default, parent `.pinstar-row:hover` shows
    //    it at full opacity (see PIN_STAR_CSS below).
    //  * pinned   → always full opacity.
    //  * alwaysVisible → bypass the hover rule.
    opacity: pinned || alwaysVisible ? 1 : 0,
    transition: 'opacity 80ms linear, color 80ms linear',
    flexShrink: 0,
  };
}

/**
 * Stylesheet snippet — inject once at app boot via your global CSS or
 * inline in a layout component. Drives the row-hover reveal without
 * touching React state per row.
 *
 * Selector contract:
 *   * `.pinstar-row` wraps each VirtualTable row (we set this on the row
 *     div from the column-render path; see below).
 *   * `.pinstar` is the button.
 *
 *   .pinstar-row:hover .pinstar { opacity: 1; }
 *   .pinstar:focus-visible { opacity: 1; outline: 1px solid var(--bd); }
 */
export const PIN_STAR_CSS = `
[role="row"]:hover .pinstar,
.pinstar:focus-visible,
.pinstar.pinstar-on,
.pinstar.pinstar-vis {
  opacity: 1;
}
.pinstar:focus-visible {
  outline: 1px solid var(--bd, rgba(255,255,255,0.18));
  outline-offset: 1px;
}
.pinstar:hover {
  color: var(--amber, #d6a93b);
}
`.trim();
